/**
 * Type Definitions: Speech Phrase-List Configuration
 * SpecReferenceID: SPEECH-V1
 * Requirements: SPEECH-V1-R10, SPEECH-V1-R11, SPEECH-V1-R12
 *
 * TypeScript interfaces for the SPEECH-V1 cardiology/ESC
 * phrase-list and normalisation-rule configuration. Mirrors
 * the Pydantic schema in
 * `src/backend/src/app/schemas/speech_phrase_config.py` so
 * the admin editor and the backend admin write path stay in
 * lockstep.
 */

/**
 * Azure's documented phrase-list size guidance, enforced
 * server-side (schema validation + a runtime defensive
 * clamp). Mirrored here so the admin form can show a live
 * counter and disable "Add" before a save round-trips a
 * validation error.
 */
export const SPEECH_PHRASE_LIST_MAX_TERMS = 500;

/**
 * A single spoken-form-to-canonical-form normalisation rule.
 * Applied to the final transcript only, case-insensitively,
 * longest-pattern-first.
 */
/**
 * One language this deployment offers for speech recognition.
 *
 * SPEECH-V1-R16: the label is stored alongside the code rather than
 * derived, so it can be served to non-browser API consumers that have no
 * locale-name facility of their own.
 */
export interface ConfiguredLanguage {
  /** BCP-47 tag, e.g. 'fr-FR'. */
  code: string;
  /** Display name, conventionally the language's own endonym. */
  label: string;
}

/** One selectable locale from the shipped catalogue. */
export interface CatalogueLocale {
  code: string;
  label: string;
}

export interface NormalizationRule {
  pattern: string;
  replacement: string;
}

/**
 * Active speech phrase-list/normalisation-rule configuration
 * blob. The admin form edits this directly and posts it back
 * to `PUT /v1/speech/phrase-config`.
 */
export interface SpeechPhraseConfig {
  /**
   * SPEECH-V1-R16: languages this deployment offers for recognition.
   * Empty array = the deployment's environment values apply, so a
   * deployment that never visits the admin page is unaffected.
   */
  languages: ConfiguredLanguage[];

  /**
   * SPEECH-V1-R16: which language applies when a user has made no
   * selection. Null = fall back to the deployment's environment value.
   * Must be one of `languages` when set; the backend rejects a default
   * outside the offered set rather than silently applying a language
   * the picker never shows.
   */
  default_language: string | null;

  /**
   * Cardiology/ESC domain terms fed to the streaming
   * session's phrase-list grammar. Empty array = no
   * phrase-list bias (a clean no-op, not an error state).
   * Capped at SPEECH_PHRASE_LIST_MAX_TERMS entries.
   */
  terms: string[];

  /**
   * Spoken-form-to-canonical-form substitution rules applied
   * to the final transcript only.
   */
  normalization_rules: NormalizationRule[];
}

/**
 * Response payload from `GET /v1/speech/phrase-config` and
 * `PUT /v1/speech/phrase-config`. Wraps `SpeechPhraseConfig`
 * with the version + audit metadata the storage layer tracks.
 */
export interface SpeechPhraseConfigApiResponse {
  config: SpeechPhraseConfig;
  version: number;
  updated_at: string;
  updated_by: string;
}

/**
 * Request payload for `PUT /v1/speech/phrase-config`.
 * Single-field wrapper so the admin form can post the whole
 * blob in one go (no partial-merge semantics).
 */
export interface SpeechPhraseConfigUpdateRequest {
  config: SpeechPhraseConfig;
}

/**
 * Safe-default SpeechPhraseConfig used as the initial form
 * state when the admin page mounts before the API call
 * resolves. The real defaults (seeded term list and
 * normalisation rules) come from the backend on load; this is
 * just an empty placeholder shape.
 */
export const DEFAULT_SPEECH_PHRASE_CONFIG: SpeechPhraseConfig = {
  languages: [],
  default_language: null,
  terms: [],
  normalization_rules: [],
};
